<template>
  <div class="duration-picker">
    <div class="duration-picker__container">
      <div class="duration-picker__field">
        <input
            id="hours"
            :class="{ 'is-invalid': hoursHasError }"
            v-model.text="hoursStr"
            type="text"
            @blur="onHoursInputBlur"
            class="duration-picker__input form-control"
        />
      </div>

      <div class="duration-picker__separator">:</div>

      <div class="duration-picker__field">
        <input
            id="minutes"
            :class="{ 'is-invalid': minutesHasError }"
            v-model.text="minutesStr"
            @blur="onMinutesInputBlur"
            type="text"
            class="duration-picker__input form-control"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, defineExpose, defineProps, ref, watch} from "vue";

const props = withDefaults(
    defineProps<{
      maxHours: number;
      isRequired: boolean;
    }>(),
    {
      maxHours: 24,
      isRequired: false,
    }
);

// Reactive data
const hoursStr = ref<string | undefined>(undefined);
const minutesStr = ref<string | undefined>(undefined);
const hoursHasError = ref<boolean>(false);
const minutesHasError = ref<boolean>(false);

// Computed
const totalSeconds = computed(() => {
  if (hoursStr.value == null || hoursStr.value === "") {
    return undefined;
  }
  return Number(hoursStr.value!) * 3600 + Number(minutesStr.value == null || minutesStr.value.length === 0 ? "0" : minutesStr.value) * 60;
});

// Methods
function validateInput(): boolean {
  hoursHasError.value = false;
  minutesHasError.value = false;

  if (props.isRequired) {
    if (hoursStr.value == null || hoursStr.value.length === 0) {
      hoursHasError.value = true;
    }
    
    if (hoursHasError.value) {
      return false;
    }
  }

  const hours = Number(hoursStr.value!);
  const minutes = Number(minutesStr.value == null || minutesStr.value.length === 0 ? "0" : minutesStr.value);

  hoursHasError.value = isNaN(hours) || hours < 0 || hours > props.maxHours;
  minutesHasError.value = isNaN(minutes) || minutes < 0 || minutes > 59;
  return !(hoursHasError.value || minutesHasError.value);
}

function getValidatedDuration(): string | undefined {
  if (validateInput()) {
    if (totalSeconds.value == null) return undefined;
    return formatDuration(totalSeconds.value);
  } else {
    return undefined;
  }
}

function onHoursInputBlur() {
  if (hoursStr.value == null || hoursStr.value!.length == 0) {
    return;
  }

  let currentHours = hoursStr.value!.replace(/\D/g, "");

  const hours = Number(currentHours);
  if (hours > props.maxHours) {
    currentHours = props.maxHours.toString();
  }

  hoursStr.value = currentHours.padStart(2, "0");
}

function onMinutesInputBlur() {
  if (minutesStr.value == null || minutesStr.value!.length == 0) {
    return;
  }

  let currentMinutes = minutesStr.value!.replace(/\D/g, "");
  const minutes = Number(currentMinutes);
  if (minutes > 59) {
    currentMinutes = "59";
  }

  if (hoursStr.value == props.maxHours.toString() && minutes > 0) {
    currentMinutes = "0";
  }

  minutesStr.value = currentMinutes.padStart(2, "0");
}

function parseDuration(totalSeconds: number) {
  hoursStr.value = String(Math.trunc(totalSeconds / 3600)).padStart(2, "0");
  minutesStr.value = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
}

function formatDuration(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400); // 86400 = 60 * 60 * 24
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return `${days}.${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  } else if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  } else {
    return `00:${String(minutes).padStart(2, "0")}:00`;
  }
}

// Public methods
function setDuration(timeStr: string | undefined) {
  if (isValidDurationRegex(timeStr)) {
    parseDuration(convertToSeconds(timeStr!));
  } else {
    hoursStr.value = undefined;
    minutesStr.value = undefined;
  }

  hoursHasError.value = false;
  minutesHasError.value = false;
}

function isValidDurationRegex(timeStr: string | undefined): boolean {
  const regex = /^(\d+\.)?([01]?\d|2[0-3]):[0-5]\d:[0-5]\d$/;
  return regex.test(timeStr ?? "");
}

function convertToSeconds(timeStr: string): number {
  // Split into days and time

  const [daysPart, timePart] = timeStr.includes(".") ? timeStr.split(".") : [undefined, timeStr];
  const [hoursPart, minutesPart] = timePart.split(":");

  const days = daysPart ? parseInt(daysPart, 10) : 0;
  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart, 10);

  // Total seconds
  return days * 24 * 3600 + hours * 3600 + minutes * 60;
}

// Watchers
watch(
    () => hoursStr.value,
    (newValue) => {
      if (newValue == null) {
        return;
      }

      newValue = newValue.replace(/\D/g, "");

      const hours = Number(newValue);
      if (hours != null && !isNaN(hours) && hours > props.maxHours) {
        newValue = props.maxHours.toString();
      }

      hoursStr.value = newValue;
    },
    {immediate: true}
);

watch(
    () => minutesStr.value,
    (newValue) => {
      if (newValue == null) {
        return;
      }

      newValue = newValue.replace(/\D/g, "");

      const minutes = Number(newValue);
      if (minutes != null && !isNaN(minutes) && minutes > 59) {
        newValue = "59";
      }

      minutesStr.value = newValue;
    },
    {immediate: true}
);

// Expose public methods
defineExpose({
  setDuration,
  getValidatedDuration,
  validateInput,
});
</script>

<style scoped>
.duration-picker__container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-picker__input {
  width: 120px;
  text-align: center;
  color: #495057;
  background: white;
}

.duration-picker__input:hover {
  border-color: #adb5bd;
}

.duration-picker__separator {
  font-size: 20px;
  font-weight: bold;
  color: #212529;
  margin: 0 4px;
  align-self: flex-end;
  margin-bottom: 8px;
}

/* Responsive design */
@media (max-width: 480px) {
  .duration-picker__container {
    padding: 12px;
    gap: 6px;
  }

  .duration-picker__input {
    width: 45px;
    height: 36px;
    font-size: 14px;
  }

  .duration-picker__separator {
    font-size: 18px;
  }
}
</style>
